import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';

// Third Party Modules
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { MsalModule, MsalInterceptor } from '@azure/msal-angular';
import { LogLevel } from 'msal';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { SwiperModule } from 'ngx-swiper-wrapper';
import { SWIPER_CONFIG } from 'ngx-swiper-wrapper';
import { SwiperConfigInterface } from 'ngx-swiper-wrapper';
import { TreeModule } from 'angular-tree-component';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

// Modules
import { DashboardModule } from './modules/dashboard/dashboard.module';

// Pages
import { DashboardPageComponent } from './modules/dashboard/pages/dashboard/dashboard.page.component';

// Components (refactor these to their own modules!)
import { environment } from '../environments/environment';
import { GraphResourceRegistry } from './services/graph-resource-registry';
import { AppComponent } from './app.component';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { GroupSalesComponent } from './group-sales/group-sales.component';
import { PROTECTED_RESOURCE_MAP } from './msal.constants';
import { HeaderComponent } from './header/header.component';
import { NavMenuRightComponent } from './nav-menu-right/nav-menu-right.component';
import { LoginComponent } from './login/login.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { GreeterComponent } from './greeter/greeter.component';
import { EventsComponent } from './events/events.component';
import { ValuesWinnersComponent } from './values-winners/values-winners.component';
import { TopPerformersComponent } from './top-performers/top-performers.component';
import { AnnouncementsComponent } from './announcements/announcements.component';
import { NewslettersComponent } from './newsletters/newsletters.component';
import { SupplierSpecialsComponent } from './supplier-specials/supplier-specials.component';
import { ServiceDirectoryComponent } from './service-directory/service-directory.component';
import { FooterComponent } from './footer/footer.component';
import { GraphClientService } from './services/graph-client-service';
import { DateHelperService } from './services/date-helper';
import { SocialMediaComponent } from './social-media/social-media.component';
import { SwiperSliderComponent } from './swiper-slider/swiper-slider.component';
import { CardDirective } from './swiper-slider/card.directive';
import { SocialCardComponent } from './social-card/social-card.component';
import { QnAComponent } from './qna/qna.component';
import { QnAService } from './services/qna-service';
import { DefaultDiagnosticsService } from './services/default-diagnostics-service';
import { DiagnosticsServiceFactory } from './services/diagnostics-service-factory';

// The following two lines are used to enable local development, isolated from O365/Azure.
import { GraphClientServiceMock } from './services/graph-client-service-mock';
import { MsalServiceMock } from './services/msal-service-mock';
import { SharedModule } from './modules/shared/shared.module';
import { WillnerPageComponent } from './modules/dashboard/pages/willner/willner.page.component';



const DEFAULT_SWIPER_CONFIG: SwiperConfigInterface = {
  direction: 'horizontal',
  slidesPerView: 4,
  spaceBetween: 25,
  keyboard: true,
  mousewheel: true,
  scrollbar: false,
  navigation: true,
  loop: false,
  pagination: false
};

export function loggerCallback(logLevel: any, message: any, piiEnabled: any) {
  // Comment or remove the following lines to disable logging from MSAL module.
  const diagnostics = new DefaultDiagnosticsService('MsalModule', {logLevel: 'DEBUG'});
  diagnostics.logDebug(message);
}

@NgModule({
  declarations: [
    AppComponent,
    NavMenuComponent,
    GroupSalesComponent,
    HeaderComponent,
    NavMenuRightComponent,
    LoginComponent,
    WelcomeComponent,
    GreeterComponent,
    EventsComponent,
    ValuesWinnersComponent,
    TopPerformersComponent,
    AnnouncementsComponent,
    NewslettersComponent,
    SupplierSpecialsComponent,
    ServiceDirectoryComponent,
    SocialMediaComponent,
    SwiperSliderComponent,
    CardDirective,
    SocialCardComponent,
    FooterComponent,
    QnAComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    MsalModule.forRoot({
      clientID: environment.azureRegistration.clientId,
      authority: environment.azureRegistration.authority,
      validateAuthority: true,
      redirectUri: environment.azureRegistration.redirectUrl,
      cacheLocation: 'localStorage',
      postLogoutRedirectUri: environment.azureRegistration.postLogoutRedirectUrl,
      navigateToLoginRequestUrl: true,
      popUp: false,
      unprotectedResources: ['/api/images/latest', '/api/kpi/overview', '/api/kpi/details', '/api/kpi/tmttoken',],
      protectedResourceMap: PROTECTED_RESOURCE_MAP,
      logger: loggerCallback,
      correlationId: '1234',
      level: LogLevel.Verbose,
      piiLoggingEnabled: true
    }),
    SwiperModule,
    NgbModule,
    TreeModule.forRoot(),
    NgxDatatableModule,
    RouterModule.forRoot([
      { path: '', component: DashboardPageComponent },
      { path: 'willner', component: WillnerPageComponent }
    ]),

    SharedModule,
    DashboardModule
  ],
  entryComponents: environment.entryComponents,
  providers: [BroadcastService,
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: GraphResourceRegistry, useClass: environment.graphResourceRegistryImpl },
    { provide: SWIPER_CONFIG, useValue: DEFAULT_SWIPER_CONFIG },
    QnAService,
    DateHelperService,
    DiagnosticsServiceFactory,
    // Dependencies configuration for local development:
    // In the following lines replace useClass: GraphClientService with
    // useClass: GraphClientServiceMock and useClass: MsalService with
    // useClass: MsalServiceMock to develop independently of O365/Azure.
    { provide: GraphClientService, useClass: GraphClientService },
    { provide: MsalService, useClass: MsalService }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
